import React, { useState, useCallback, useEffect } from 'react'
import { debounce } from 'lodash'

import CircularProgress from '@mui/material/CircularProgress'
import Modal from '@mui/material/Modal'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { formatNumber3Decimals } from '../../../../util/formats'

import { updateTransactions, getAllBulkById } from '../../../../contexts/airdropActionCreators'
import { selectedRowType } from '../../types'
import SettingTitle from '../../../../components/kit/SettingTitle'
import ModalInputField from '../../../../components/kit/ModalInputField'
import UiButton from 'src/ui/atoms/Button'
import './UpdateModal.scss'

export default function UpdateModal({
  showUpdateModal,
  toggleUpdateModal,
  selectedRow,
  setTransactions,
  id,
}: {
  showUpdateModal: boolean
  toggleUpdateModal: () => void
  selectedRow: selectedRowType | null
  setTransactions: (transactions: []) => void
  id: any
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [receiver, setReceiver] = useState(selectedRow?.receiver ?? '')
  const [amount, setAmount] = useState(selectedRow?.amount ?? 0)
  const [displayedAmount, setDisplayedAmount] = useState(
    formatNumber3Decimals(selectedRow?.amount ?? 0),
  )

  useEffect(() => {
    if (selectedRow) {
      setReceiver(selectedRow?.receiver ?? '')
      setAmount(selectedRow?.amount ?? 0)
      setDisplayedAmount(formatNumber3Decimals(selectedRow?.amount ?? '0'))
    }
  }, [selectedRow])

  const debouncedAmount = useCallback(
    debounce((value) => {
      setDisplayedAmount(formatNumber3Decimals(value))
    }, 800),
    [],
  )

  const handleChangeAmount = (value: string) => {
    const val = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : parseFloat(value)
    const amount = val && !isNaN(val) && val > 0 ? val : 0

    setAmount(amount)
    setDisplayedAmount(value)
    debouncedAmount(amount)
  }

  const handleUpdateTransaction = async () => {
    try {
      setIsLoading(true)
      await updateTransactions([
        {
          id: selectedRow?.id as string,
          // receiverName,
          receiver,
          amount,
        },
      ])
      const res = await getAllBulkById(id)
      setTransactions(res.transactions)
      setIsLoading(false)
      toggleUpdateModal()
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  useEffect(() => {
    if (!showUpdateModal) {
      setReceiver('')
      setAmount(0)
      setDisplayedAmount('0')
    }
  }, [showUpdateModal])

  return (
    <Modal
      open={showUpdateModal}
      onClose={toggleUpdateModal}
      aria-labelledby="update-transaction-modal"
      aria-describedby="update-transaction-modal">
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minHeight: '400px',
          maxHeight: '90%',
          width: '580px',
          maxWidth: '90%',
          background: '#181D38',
          color: '#fff',
          borderRadius: '16px',
        }}>
        <Box
          py={3}
          px={3}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            borderRadius: '8px',
            boxSizing: 'border-box',
          }}>
          <Typography
            variant="h5"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              marginBottom: '2rem',
              color: '#fff',
              fontFamily: 'SpaceGrotesk, sans-serif',
            }}>
            Update transaction
          </Typography>
          <div className="propertyContainer">
            <SettingTitle title="Receiver:" />
            <ModalInputField
              value={receiver}
              onChange={setReceiver}
              disabled={selectedRow?.status === 'completed' || selectedRow?.status === 'pending'}
            />
          </div>
          <div className="propertyContainer">
            <SettingTitle title="Amount:" />
            <ModalInputField
              value={displayedAmount}
              onChange={handleChangeAmount}
              disabled={selectedRow?.status === 'completed' || selectedRow?.status === 'pending'}
            />
          </div>
          <Box
            mt={2}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              gap: '40px',
            }}>
            <UiButton name="Cancel" size="M" onClick={toggleUpdateModal} type="secondary" />
            <Button
              className="btn btn-primary"
              color="success"
              size="medium"
              variant="contained"
              onClick={handleUpdateTransaction}
              disabled={
                isLoading ||
                selectedRow?.status === 'completed' ||
                selectedRow?.status === 'pending'
              }>
              Update
              {isLoading ? (
                <CircularProgress color="secondary" size="2rem" sx={{ marginLeft: '1rem' }} />
              ) : null}
            </Button>
          </Box>
        </Box>
      </Card>
    </Modal>
  )
}
